import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Download, Play, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const COLUMN_LABELS = {
  name: 'Name', email: 'Email', phone: 'Phone', country: 'Country',
  has_mttf_postnatal_yoga: 'Has batch MTTF- Postnatal Yoga',
  course_expiry_date: 'Course expiry date', weeks_since_delivery: 'Weeks since delivery',
  edd: 'EDD', window_ended_on: 'Window ended on (EDD+7)', learner_type: 'Tier',
  batches: 'Batches', courses: 'Courses', action: 'Action',
};

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const PrenatalWindowCleanupCard = () => {
  const [busy, setBusy] = useState(null); // 'dry' | 'apply'
  const [result, setResult] = useState(null);

  const call = async (apply) => {
    setBusy(apply ? 'apply' : 'dry');
    try {
      const res = await api.post(`/api/admin/ops/postnatal-downgrade?apply=${apply}`);
      setResult(res.data);
      const s = res.data.summary || {};
      toast.success(apply
        ? `Downgraded ${s.downgraded} learner(s) to free tier`
        : `Dry run: ${s.eligible} learner(s) would be downgraded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Run failed');
    }
    setBusy(null);
  };

  const download = () => {
    if (!result?.rows?.length) return;
    const cols = result.columns || Object.keys(result.rows[0]);
    const csv = [cols.map((c) => COLUMN_LABELS[c] || c).join(','),
      ...result.rows.map((r) => cols.map((c) => csvCell(r[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `prenatal-window-edd-plus-7-${result.summary?.today || 'report'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const apply = () => {
    const s = result?.summary;
    if (!s) { toast.error('Run the dry run first.'); return; }
    if (!window.confirm(
      `Downgrade ${s.eligible} learner(s) to the free tier?\n\n` +
      'Their batches and courses will be removed and the SoulAI Coach ticket revoked. ' +
      'Accounts stay active, nothing is suspended, no one is notified. This cannot be undone in bulk.',
    )) return;
    call(true);
  };

  const s = result?.summary;

  return (
    <Card className="rounded-2xl border-amber-200/70" data-testid="prenatal-window-cleanup-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />Prenatal window cleanup (EDD + 7)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Finds every learner whose prenatal programme window (EDD + 7 days) has already closed.
          Moms in <strong>MTTF- Postnatal Yoga</strong> — or with an OPS course-expiry date still to come —
          are skipped. Everyone else is brought down to the <strong>free tier</strong>: all batches released,
          all courses and the prenatal SoulAI Coach revoked, account left active and unsuspended, nobody notified.
          Baby Tracker follows the founder rule — 12 months if it was ticked on the prenatal payment,
          30-day trial if she was Elite, else 7 days, starting at her first postnatal login.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => call(false)} disabled={!!busy}
            data-testid="prenatal-cleanup-dry-run-btn">
            {busy === 'dry' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Dry run
          </Button>
          <Button variant="outline" onClick={download} disabled={!result?.rows?.length}
            data-testid="prenatal-cleanup-download-btn">
            <Download className="h-4 w-4 mr-2" />Download CSV{result?.rows?.length ? ` (${result.rows.length})` : ''}
          </Button>
          <Button onClick={apply} disabled={!!busy || !s?.eligible}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="prenatal-cleanup-apply-btn">
            {busy === 'apply' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Apply to {s?.eligible ?? 0}
          </Button>
        </div>

        {s && (
          <div className="rounded-xl bg-muted/50 p-3 space-y-2" data-testid="prenatal-cleanup-summary">
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="secondary">Scanned {s.scanned}</Badge>
              <Badge variant="secondary">Eligible {s.eligible}</Badge>
              <Badge variant="secondary">Has MTTF (skipped) {s.skipped_has_postnatal_batch}</Badge>
              <Badge variant="secondary">Renewed / expiry pending {s.skipped_active_expiry}</Badge>
              <Badge variant="secondary">Already done {s.skipped_already_done}</Badge>
              {s.applied && <Badge className="bg-emerald-700">Downgraded {s.downgraded}</Badge>}
              {s.applied && <Badge variant="secondary">Baby Tracker granted {s.baby_tracker_granted}</Badge>}
              {s.applied && <Badge variant="secondary">Baby Tracker trial {s.baby_tracker_trial}</Badge>}
              {!!s.failed && <Badge variant="destructive">Failed {s.failed}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">
              {s.applied ? 'Applied' : 'Dry run'} · window computed on {s.today} (IST)
            </p>
          </div>
        )}

        {!!result?.rows?.length && (
          <div className="max-h-72 overflow-auto rounded-xl border" data-testid="prenatal-cleanup-table">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 sticky top-0">
                <tr>
                  {['Name', 'Email', 'MTTF', 'Weeks since delivery', 'Action'].map((h) => (
                    <th key={h} className="text-left font-medium px-2 py-1.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.slice(0, 200).map((r) => (
                  <tr key={r.user_id} className="border-t">
                    <td className="px-2 py-1.5">{r.name}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{r.email}</td>
                    <td className="px-2 py-1.5">{r.has_mttf_postnatal_yoga}</td>
                    <td className="px-2 py-1.5">{r.weeks_since_delivery}</td>
                    <td className="px-2 py-1.5">{r.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {result.rows.length > 200 && (
              <p className="text-[11px] text-muted-foreground px-2 py-1.5">
                Showing first 200 of {result.rows.length} — the CSV has everything.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
